import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import moment from "moment-timezone";
import { formatCurrency } from "../../../utils/format/currency";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
interface ModalLendRefinanceProps {
  open: boolean;
  onClose: () => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  clientLend?: {
    boxId: number;
    date_start: string;
    date_end: string;
    collect: string;
    collect_id: string;
  } | null;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 1000,
  height: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const ModalLendRefinance: React.FC<ModalLendRefinanceProps> = ({
  open,
  onClose,
  clientLend,
  showAlert,
}) => {
  const [movement, setMovement] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  const lendKey = useMemo(() => {
    if (!clientLend) return "";
    return [
      clientLend.boxId,
      moment(clientLend.date_start).format("YYYY-MM-DD"),
      moment(clientLend.date_end).format("YYYY-MM-DD"),
      String(clientLend.collect_id),
    ].join("|");
  }, [
    clientLend?.boxId,
    clientLend?.date_start,
    clientLend?.date_end,
    clientLend?.collect_id,
  ]);

  useEffect(() => {
    if (!open) {
      setMovement([]);
      setIsFetching(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !clientLend || !lendKey) return;

    const ac = new AbortController();
    setIsFetching(true);
    setMovement([]);

    const fetchData = async () => {
      try {
        const formData = {
          boxId: clientLend.boxId,
          date_start: moment(clientLend.date_start).format("YYYY-MM-DD"),
          date_end: moment(clientLend.date_end).format("YYYY-MM-DD"),
          collect_id: clientLend.collect_id,
          collect: clientLend.collect,
        };
        const { data } = await axios.post("/report/modal/lendrefinance", formData, {
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;

        const records = Array.isArray(data?.data) ? data.data : [];
        const filtered = records.filter(Boolean);
        setMovement(filtered);
        if (filtered.length === 0) {
          showAlertRef.current("No se encontraron registros para esta consulta.", "info");
        } else {
          showAlertRef.current(
            `Se encontraron ${filtered.length} registro${filtered.length === 1 ? "" : "s"}.`,
            "success"
          );
        }
      } catch (error: unknown) {
        if (ac.signal.aborted) return;
        console.error("Error al cargar datos del modal:", error);
        showAlertRef.current("Error al cargar los refinanciados.", "error");
        setMovement([]);
      } finally {
        if (!ac.signal.aborted) {
          setIsFetching(false);
        }
      }
    };

    fetchData();

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lendKey]);

  const rows = useMemo(() => {
    const parsed = (Array.isArray(movement) ? movement : []).map((data) => ({
      ...data,
      id: data?.id ?? Math.random(),
      client: data?.client?.fullname || "",
      utility: Number(data?.utility) || 0,
      value: Number(data?.value_start) || 0,
      balance: Number(data?.balance) || 0,
      value_refinance: Number(data?.value) || 0,
    }));

    const totalValue = parsed.reduce((sum, item) => sum + item.value, 0);
    const totalUtility = parsed.reduce((sum, item) => sum + item.utility, 0);
    const totalRefinance = parsed.reduce((sum, item) => sum + item.value_refinance, 0);
    const totalBalance = parsed.reduce((sum, item) => sum + item.balance, 0);

    return [
      ...parsed.map((r) => ({
        ...r,
        value_fmt: formatCurrency(r.value),
        utility_fmt: formatCurrency(r.utility),
        balance_fmt: formatCurrency(r.balance),
        value_refinance_fmt: formatCurrency(r.value_refinance),
      })),
      {
        id: "total-row",
        client: "TOTAL",
        value_fmt: formatCurrency(totalValue),
        utility_fmt: formatCurrency(totalUtility),
        balance_fmt: formatCurrency(totalBalance),
        value_refinance_fmt: formatCurrency(totalRefinance),
      },
    ];
  }, [movement]);

  const columns: GridColDef[] = [
    { field: "id", headerName: "Prestamo", width:60},
    { field: "client", headerName: "Cliente", width:340},
    { field: "utility_fmt", headerName: "Utilidad", width:120},
    { field: "value_fmt", headerName: "Valor Prestamo",width:120},
    { field: "balance_fmt", headerName: "Saldo",width:120},
    { field: "value_refinance_fmt", headerName: "Valor Refinanciado", flex: 1 },
  ];
  return (
    <Modal open={open} onClose={onClose}>
      <Paper sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6">Prestamos Refinanciados</Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: "white" }}
            aria-label="cerrar"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Cobro - {clientLend?.collect || "Cobro"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">
                <strong>Fechas:</strong> {clientLend?.date_start} - {clientLend?.date_end}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ position: "relative", minHeight: 300, width: "100%" }}>
            {isFetching && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.75)",
                  zIndex: 2,
                  borderRadius: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id || Math.random()}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  csvOptions: {
                    fileName: `resumen-${nowOperationalDateString()}`,
                    utf8WithBom: true,
                  },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                },
              }}
              hideFooter
              autoHeight
              density="compact"
              disableColumnFilter
              disableColumnMenu
              disableRowSelectionOnClick
              disableColumnSelector
              disableDensitySelector
              disableVirtualization
              sx={{
                '& .MuiDataGrid-root': { border: '1px solid #1976d2' },
                '& .MuiDataGrid-toolbarContainer': {
                  border: '1px solid #1976d2',
                  backgroundColor: '#f5faff',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#1976d2',
                  '& .MuiDataGrid-columnHeader': {
                    borderRight: '1px solid #1976d2',
                    '&:last-child': { borderRight: 'none' },
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderRight: '1px solid #1976d2',
                  borderLeft: '2px solid #1976d2',
                  borderBottom: '1px solid #1976d2',
                  '&:last-child': { borderRight: 'none' },
                },
                '& .MuiDataGrid-row': {
                  borderBottom: '1px solid #1976d2',
                  '&:last-child': {
                    borderBottom: '1px solid #1976d2',
                    backgroundColor: '#fafafa',
                    borderTop: '2px solid #1976d2',
                  },
                },
                '& .header-style': {
                  fontWeight: 'bold',
                  color: '#212121',
                },
                '& .MuiDataGrid-columnSeparator': { display: 'none' },
                '& .footer-total .MuiDataGrid-cell': {
                  justifyContent: 'center',
                  backgroundColor: '#e3f2fd',
                  textAlign: 'center',
                  fontWeight: 'bold',
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Box>
            <Button variant="outlined" onClick={onClose} sx={{ mr: 1 }}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ModalLendRefinance;
